/**
 * View Mode Toggle
 *
 * Switch between card and list view modes.
 * PURELY PRESENTATIONAL - no business logic.
 */

export type ViewMode = 'cards' | 'list' | 'calendar' | 'map';

interface ViewModeToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

export function ViewModeToggle({ mode, onChange }: ViewModeToggleProps) {
  return (
    <div className="inline-flex rounded-lg border border-neutral-300 bg-white p-1">
      <button
        onClick={() => onChange('cards')}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          mode === 'cards'
            ? 'bg-primary-500 text-white'
            : 'text-neutral-700 hover:bg-neutral-100'
        }`}
        aria-label="Card view"
        aria-pressed={mode === 'cards'}
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
            />
          </svg>
          <span>Cards</span>
        </div>
      </button>

      <button
        onClick={() => onChange('list')}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          mode === 'list'
            ? 'bg-primary-500 text-white'
            : 'text-neutral-700 hover:bg-neutral-100'
        }`}
        aria-label="List view"
        aria-pressed={mode === 'list'}
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
          <span>List</span>
        </div>
      </button>

      <button
        onClick={() => onChange('calendar')}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          mode === 'calendar'
            ? 'bg-primary-500 text-white'
            : 'text-neutral-700 hover:bg-neutral-100'
        }`}
        aria-label="Calendar view"
        aria-pressed={mode === 'calendar'}
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>Calendar</span>
        </div>
      </button>

      <button
        onClick={() => onChange('map')}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          mode === 'map'
            ? 'bg-primary-500 text-white'
            : 'text-neutral-700 hover:bg-neutral-100'
        }`}
        aria-label="Map view"
        aria-pressed={mode === 'map'}
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <span>Map</span>
        </div>
      </button>
    </div>
  );
}
